// Histórico de preços de um Tipo de Peça — as vigências do preço base e do
// preço próprio de cada Cliente (as duas com a mesma tabela: corrigir
// data/valor, remover, adicionar) mais a linha do tempo do preço cobrado.
// Acessível pela Tabela de Preços (ADR-0006: não mora mais no Tipo de Peça).

import {
  linhaDoTempoPreco,
  precoVigente,
  vigenciasPreco,
  type VigenciaPreco,
} from "../../domain/precificacao.ts";
import { calcularImpactoPrecoRetroativo } from "../../domain/faturamento.ts";
import { formatReais } from "../../domain/money.ts";
import { agoraISO, formatData, hojeISO, isDataISO } from "../../domain/datas.ts";
import type { Cliente, Snapshot } from "../../domain/snapshot.ts";
import { executarComando, type Comando } from "../../comandos/index.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";
import { abrirConfirmacao, inputData } from "./_cadastro.ts";
import { inputMoeda } from "./_input-moeda.ts";
import { podeEditarModulo } from "./_permissao.ts";

/** `<ul>` com a linha do tempo do preço (recente primeiro). */
export function linhaDoTempoElemento(
  snapshot: Snapshot,
  tipoId: string,
  clienteId: string | null,
): HTMLElement {
  const segs = linhaDoTempoPreco(snapshot, tipoId, clienteId, hojeISO());
  const tl = el("ul", { class: "hist-tl" });
  if (segs.length === 0) {
    tl.append(el("li", { class: "hist-tl-vazio" }, "Sem histórico de preço."));
    return tl;
  }
  for (const s of [...segs].reverse()) {
    tl.append(
      el(
        "li",
        s.vigente ? { class: "vig" } : {},
        el(
          "span",
          { class: "hist-tl-top" },
          el(
            "b",
            { class: "num" },
            s.valorCentavos == null ? "Sem Preço" : formatReais(s.valorCentavos),
          ),
          el("span", { class: "hist-tl-date num" }, `Desde ${formatData(s.desde)}`),
        ),
        el(
          "span",
          { class: "hist-tl-src" },
          s.origem === "cliente" ? "Preço próprio do cliente" : "Preço base",
        ),
      ),
    );
  }
  return tl;
}

/**
 * Modal com o histórico de um preço. `clienteId` (a grade da Tabela de
 * Preços com um Cliente escolhido) já abre o histórico desse Cliente — mesmo
 * sem preço próprio ainda, para poder incluir o primeiro com a data certa.
 */
export function abrirHistoricoPreco(store: Store, tipoId: string, clienteId: string | null = null): void {
  const tipo = store.get().tiposPeca.find((t) => t.id === tipoId);
  openModal(`Histórico de Preço — ${tipo?.nome ?? ""}`, () =>
    el("div", { class: "hist-modal" }, secaoHistoricoPreco(store, tipoId, clienteId)),
  );
}

/**
 * De quem é a vigência: do preço base do Tipo, ou do preço próprio de um
 * Cliente. A tabela de vigências (Corrigir data/valor, Remover, Adicionar) é
 * a mesma para os dois — só o comando muda.
 */
type EscopoVigencia = { tipo: "base" } | { tipo: "cliente"; clienteId: string };

/**
 * Tudo o que muda entre base e Cliente — o único lugar que escolhe entre os
 * dois: a chave do escopo (estado da tela), o rótulo do campo de preço novo e
 * os comandos.
 */
function porEscopo(tipoId: string, e: EscopoVigencia) {
  return {
    chave: e.tipo === "base" ? "base" : `cliente:${e.clienteId}`,
    rotuloNovoPreco: e.tipo === "base" ? "Novo preço base" : "Novo preço do cliente",
    adicionar: (desde: string, centavos: number): Comando => ({
      agregado: "preco",
      cmd: {
        tipo: "aplicarTabela",
        desde,
        itens: [
          e.tipo === "base"
            ? { escopo: "base", tipoId, centavos }
            : { escopo: "cliente", clienteId: e.clienteId, tipoId, centavos },
        ],
      },
    }),
    corrigir: (de: string, para: string, centavos: number): Comando =>
      e.tipo === "base"
        ? { agregado: "preco", cmd: { tipo: "corrigirVigenciaBase", tipoId, de, para, centavos } }
        : {
            agregado: "preco",
            cmd: { tipo: "corrigirVigenciaCliente", clienteId: e.clienteId, tipoId, de, para, centavos },
          },
    remover: (desde: string): Comando =>
      e.tipo === "base"
        ? { agregado: "preco", cmd: { tipo: "removerVigenciaBase", tipoId, desde } }
        : { agregado: "preco", cmd: { tipo: "removerVigenciaCliente", clienteId: e.clienteId, tipoId, desde } },
  };
}

export function secaoHistoricoPreco(
  store: Store,
  tipoId: string,
  clienteFoco: string | null = null,
): HTMLElement {
  const root = el("div", { class: "hist-wrap" });
  // Permissão de Módulo (issue #71): correção/adição de vigência é escrita
  // em Configuração — o resto da tela (linha do tempo, preços por cliente) é
  // só leitura e continua sempre visível.
  const podeGravar = podeEditarModulo(store, "configuracao");
  // clienteIds com o histórico aberto; o Cliente em foco já abre aberto
  const abertos = new Set<string>(clienteFoco ? [clienteFoco] : []);
  // a vigência em edição (escopo + data em que começa) e o escopo adicionando
  let corrigindo: { chave: string; desde: string } | null = null;
  let adicionando: string | null = null;

  const render = (): void => {
    clear(root);
    const snap = store.get();
    if (!snap.tiposPeca.some((t) => t.id === tipoId)) return;
    const hoje = hojeISO();

    root.append(
      el(
        "section",
        { class: "hist" },
        el("h5", {}, "Histórico do preço base"),
        ...tabelaVigencias({ tipo: "base" }, vigenciasPreco(snap, tipoId, null, hoje), "Sem preço base cadastrado."),
      ),
    );

    // quem tem preço próprio deste Tipo, mais o Cliente em foco (mesmo sem)
    const ids = new Set(snap.precosCliente.filter((p) => p.tipoId === tipoId).map((p) => p.clienteId));
    if (clienteFoco) ids.add(clienteFoco);
    const clientes = [...ids]
      .map((id) => snap.clientes.find((c) => c.id === id))
      .filter((c): c is Cliente => c != null)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    if (clientes.length > 0) {
      const sec = el("section", { class: "hist" }, el("h5", {}, "Preços por cliente"));
      for (const c of clientes) sec.append(linhaCliente(snap, c, hoje));
      root.append(sec);
    }
  };

  /**
   * Tabela de vigências de um escopo (+ o link de adicionar, se pode gravar).
   * Sem vigência nenhuma e sem estar adicionando, só o aviso `vazio`.
   */
  const tabelaVigencias = (
    escopo: EscopoVigencia,
    vigencias: readonly VigenciaPreco[],
    vazio: string,
  ): HTMLElement[] => {
    const chave = porEscopo(tipoId, escopo).chave;
    const link = adicionando !== chave && podeGravar ? [linkAdicionar(escopo)] : [];
    if (vigencias.length === 0 && adicionando !== chave) {
      return [el("p", { class: "muted mini-vazio" }, vazio), ...link];
    }
    const corpo = vigencias.map((p) => linhaVigencia(escopo, p));
    if (adicionando === chave) corpo.unshift(linhaNovaVigencia(escopo));
    const tabela = el(
      "table",
      { class: "mini" },
      el(
        "thead",
        {},
        el(
          "tr",
          {},
          el("th", {}, "A partir de"),
          el("th", { class: "r" }, "Preço"),
          el("th", { class: "r" }, ""),
        ),
      ),
      el("tbody", {}, ...corpo),
    );
    return [tabela, ...link];
  };

  const linkAdicionar = (escopo: EscopoVigencia): HTMLElement => {
    const b = botao(
      "link",
      {},
      "+ Adicionar Vigência",
    );
    b.addEventListener("click", () => {
      adicionando = porEscopo(tipoId, escopo).chave;
      corrigindo = null;
      render();
    });
    return b;
  };

  /**
   * Grava uma mudança de vigência (base ou do Cliente): incluir, corrigir ou
   * remover. Se `dataAlvo` já passou, simula o comando em memória (sem
   * gravar) para medir o impacto em OSs já entregues e não faturadas, e só
   * pede confirmação quando há algo a confirmar — mudança de valor nenhuma,
   * ou nenhuma OS afetada, grava direto. Se a própria simulação falhar
   * (comando inválido), deixa o caminho normal tratar o erro — a mensagem de
   * validação sai igual, só sem prévia.
   */
  const rodarComPrevia = (
    cmd: Comando,
    aoConfirmar: () => void,
    dataAlvo: string,
    erro: HTMLElement,
    msgOk: string,
  ): void => {
    const acao = criarAcaoComando(store, erro);
    const hoje = hojeISO();
    if (dataAlvo >= hoje) {
      void acao(cmd, msgOk, aoConfirmar);
      return;
    }
    const snapAntigo = store.get();
    let snapNovo: Snapshot;
    try {
      snapNovo = executarComando(snapAntigo, cmd, { hoje, agora: agoraISO() });
    } catch {
      void acao(cmd, msgOk, aoConfirmar);
      return;
    }
    const previa = calcularImpactoPrecoRetroativo(snapAntigo, snapNovo, dataAlvo);
    const afetadas = previa.ossAbertasAfetadas + previa.compensacoesAfetadas;
    if (previa.deltaCentavos === 0 && afetadas === 0) {
      void acao(cmd, msgOk, aoConfirmar);
      return;
    }
    const diffReais = formatReais(Math.abs(previa.deltaCentavos));
    const sinal = previa.deltaCentavos > 0 ? "AUMENTO" : "REDUÇÃO";
    // o que muda: OSs entregues e não faturadas e/ou Compensações de Peças
    // de faturas ainda não emitidas (ADR-0031)
    const partes: (Node | string)[] = [];
    if (previa.ossAbertasAfetadas > 0) {
      partes.push(el("strong", {}, String(previa.ossAbertasAfetadas)), " OS(s) já entregues e ainda não faturadas");
    }
    if (previa.compensacoesAfetadas > 0) {
      if (partes.length > 0) partes.push(" e ");
      partes.push(
        el("strong", {}, String(previa.compensacoesAfetadas)),
        " Compensação(ões) de Peças em fatura ainda não emitida",
      );
    }
    const mensagem = el(
      "span",
      {},
      "Atenção: essa mudança afeta ",
      ...partes,
      ". Haverá um ",
      el("strong", {}, sinal),
      " de ",
      el("strong", {}, diffReais),
      " no total a faturar. Confirmar alteração?",
    );

    abrirConfirmacao({
      titulo: "Impacto Financeiro Retroativo",
      mensagem,
      rotuloConfirmar: "Confirmar",
      aoConfirmar: () => void acao(cmd, msgOk, aoConfirmar),
    });
  };

  const linhaNovaVigencia = (escopo: EscopoVigencia): HTMLElement => {
    const cmds = porEscopo(tipoId, escopo);
    const dataInp = inputData(hojeISO(), { class: "hist-data" });
    const inp = inputMoeda({ ariaLabel: cmds.rotuloNovoPreco });
    const erro = el("span", { class: "hist-erro" });
    const ok = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Adicionar");
    const cancelar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Cancelar");
    ok.addEventListener("click", () => {
      const v = inp.centavos();
      if (v == null || v === 0) {
        erro.textContent = "Informe o preço.";
        return;
      }
      if (!isDataISO(dataInp.value)) {
        erro.textContent = "Data inválida.";
        return;
      }
      rodarComPrevia(
        cmds.adicionar(dataInp.value, v),
        () => {
          adicionando = null;
          render();
        },
        dataInp.value,
        erro,
        "Vigência salva.",
      );
    });
    cancelar.addEventListener("click", () => {
      adicionando = null;
      render();
    });
    return el(
      "tr",
      { class: "editando" },
      el(
        "td",
        { colspan: "3" },
        el("div", { class: "hist-edit" }, dataInp, inp.element, ok, cancelar, erro),
      ),
    );
  };

  const linhaVigencia = (escopo: EscopoVigencia, p: VigenciaPreco): HTMLElement => {
    const cmds = porEscopo(tipoId, escopo);
    const chave = cmds.chave;
    if (corrigindo?.chave === chave && corrigindo.desde === p.desde) {
      const dataInp = inputData(p.desde, { class: "hist-data" });
      const inp = inputMoeda({
        centavos: p.valorCentavos,
        ariaLabel: "Preço corrigido",
      });
      const erro = el("span", { class: "hist-erro" });
      const salvar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Salvar");
      const remover = botao(
        { variante: "perigo", tamanho: "compacto" },
        {},
        "Remover",
      );
      const cancelar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Cancelar");
      const concluir = (): void => {
        corrigindo = null;
        render();
      };
      salvar.addEventListener("click", () => {
        const v = inp.centavos();
        if (v == null || v === 0) {
          erro.textContent = "Informe o preço.";
          return;
        }
        const novaData = isDataISO(dataInp.value) ? dataInp.value : p.desde;
        const alvoMenor = novaData < p.desde ? novaData : p.desde;
        rodarComPrevia(cmds.corrigir(p.desde, novaData, v), concluir, alvoMenor, erro, "Vigência salva.");
      });
      // remover uma vigência já começada também muda o preço de OSs entregues
      remover.addEventListener("click", () =>
        rodarComPrevia(cmds.remover(p.desde), concluir, p.desde, erro, "Vigência removida."),
      );
      cancelar.addEventListener("click", concluir);
      return el(
        "tr",
        { class: "editando" },
        el(
          "td",
          { colspan: "3" },
          el(
            "div",
            { class: "hist-edit" },
            dataInp,
            inp.element,
            salvar,
            remover,
            cancelar,
            erro,
          ),
        ),
      );
    }

    const corrigir = botao("link", {}, "Corrigir");
    corrigir.addEventListener("click", () => {
      corrigindo = { chave, desde: p.desde };
      adicionando = null;
      render();
    });
    return el(
      "tr",
      p.vigente ? {} : { class: "antiga" },
      el("td", { class: "num" }, formatData(p.desde)),
      el("td", { class: "r num" }, formatReais(p.valorCentavos)),
      el(
        "td",
        { class: "r" },
        el(
          "span",
          { class: "hist-acao" },
          p.vigente ? el("span", { class: "pill-ok" }, "Vigente") : null,
          podeGravar ? corrigir : null,
        ),
      ),
    );
  };

  const linhaCliente = (snap: Snapshot, c: Cliente, hoje: string): HTMLElement => {
    const escopo: EscopoVigencia = { tipo: "cliente", clienteId: c.id };
    const efetivo = precoVigente(snap, c.id, tipoId, hoje);
    const aberto = abertos.has(c.id);
    const toggle = botao(
      "link",
      {},
      aberto ? "Ocultar" : "Ver Histórico",
    );
    toggle.addEventListener("click", () => {
      if (aberto) {
        abertos.delete(c.id);
        // fechar descarta a edição que estava aberta neste Cliente
        const chave = porEscopo(tipoId, escopo).chave;
        if (corrigindo?.chave === chave) corrigindo = null;
        if (adicionando === chave) adicionando = null;
      } else {
        abertos.add(c.id);
      }
      render();
    });
    const linha = el(
      "div",
      { class: "cliente-row" },
      el("span", { class: "cliente-nome" }, c.nome),
      efetivo.pendente
        ? el("span", { class: "pill-mut" }, "Sem Preço")
        : el(
            "span",
            { class: "pill-mut" },
            efetivo.origem === "cliente" ? "Preço Próprio" : "Usa o Base",
          ),
      el(
        "span",
        { class: "cliente-val num" },
        efetivo.pendente ? "—" : formatReais(efetivo.valorCentavos),
      ),
      toggle,
    );
    if (!aberto) return linha;
    return el(
      "div",
      { class: "cliente-detalhe" },
      linha,
      el("h6", {}, "Preço próprio do cliente"),
      ...tabelaVigencias(
        escopo,
        vigenciasPreco(snap, tipoId, c.id, hoje),
        "Sem preço próprio — usa o preço base.",
      ),
      el("h6", {}, "Preço cobrado ao longo do tempo"),
      linhaDoTempoElemento(snap, tipoId, c.id),
    );
  };

  render();
  return root;
}
